/**
 * Wave 2 of the appointment-file reads cutover
 * (`specs/appointment-file-reads-portal-cutover.md`): the batch readers read
 * files from `POST /appointments/files/batch`, behind
 * `USE_ECS_FILE_READS_BATCH`.
 *
 * `appointmentFilesBatchQueryConfig` has `appointmentFilesQueryConfig`'s shape,
 * so each reader swaps one for the other by flag. Rows requested in the same
 * render go out as one batch request through `loadAppointmentFiles`.
 */
import { type UseQueryResult, useQueries } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  appointmentFilesKeys,
  appointmentFilesQueryConfig,
  type FileDisplayData,
} from "~/api/queries/useAppointmentFiles";
import {
  loadAppointmentFiles,
  reportFileReadDivergence,
  toFileDisplayData,
} from "~/services/appointmentFiles";
import { FEATURE_FLAGS } from "~/services/featureFlags/constants";
import { useFeatureFlag } from "~/services/featureFlags/provider";

/**
 * Whether the Wave 2 readers read through the api-server. Readers start no
 * file read while `loading`: `useFeatureFlag` resolves per hook instance after
 * the first render, so reading "legacy until known" would fire one junction
 * read per dash row that the batch read then replaces.
 */
export function useBatchFileReads(): { isEnabled: boolean; loading: boolean } {
  const { isEnabled, loading } = useFeatureFlag(
    FEATURE_FLAGS.USE_ECS_FILE_READS_BATCH,
  );
  return { isEnabled, loading };
}

const shadowCompared = new Set<string>();

/**
 * Compare against the junction read once per appointment per session, in the
 * background, for the soak. Siblings' read-only files the junction never had
 * are the accepted widening, so they are left out rather than reported.
 */
async function shadowCompare(
  appointmentId: string,
  apiFiles: FileDisplayData[],
): Promise<void> {
  if (shadowCompared.has(appointmentId)) return;
  shadowCompared.add(appointmentId);

  let legacy: FileDisplayData[];
  try {
    legacy = await appointmentFilesQueryConfig(appointmentId).queryFn();
  } catch {
    // Best-effort: a failed legacy read is not a divergence.
    return;
  }
  const legacyIds = new Set(legacy.map((f) => f.id));
  const comparable = apiFiles.filter(
    (f) => legacyIds.has(f.id) || !f.isReadOnly,
  );
  reportFileReadDivergence("batch", appointmentId, legacy, comparable);
}

/**
 * One appointment's files via the batch endpoint. A `null` entry (forbidden,
 * or the server failed that appointment) falls back to the junction read for
 * that row only.
 */
export function appointmentFilesBatchQueryConfig(appointmentId: string) {
  return {
    queryKey: appointmentFilesKeys.api(appointmentId),
    queryFn: async (): Promise<FileDisplayData[]> => {
      const files = await loadAppointmentFiles(appointmentId);
      if (files === null) {
        return appointmentFilesQueryConfig(appointmentId).queryFn();
      }
      const mapped = toFileDisplayData(appointmentId, files);
      void shadowCompare(appointmentId, mapped);
      return mapped;
    },
    staleTime: 5 * 60 * 1000,
  };
}

interface FilesByAppointment {
  filesByAppointment: Map<string, FileDisplayData[]>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Files for many appointments through the batch endpoint, as a map — the
 * api-server counterpart of `useAllAppointmentFiles` for readers that take the
 * per-appointment lists as they are. Pass `[]` to disable.
 */
export function useAppointmentFilesBatch(
  appointmentIds: string[],
): FilesByAppointment {
  // Stable combine — see useAllAppointmentFiles.
  const combine = useCallback(
    (results: UseQueryResult<FileDisplayData[], Error>[]) => {
      const filesByAppointment = new Map<string, FileDisplayData[]>();
      for (let i = 0; i < appointmentIds.length; i++) {
        filesByAppointment.set(appointmentIds[i], results[i]?.data ?? []);
      }
      return {
        filesByAppointment,
        isLoading: results.some((q) => q.isLoading),
        error: results.find((q) => q.error)?.error ?? null,
      };
    },
    [appointmentIds],
  );

  return useQueries({
    queries: appointmentIds.map((id) => appointmentFilesBatchQueryConfig(id)),
    combine,
  });
}
